import { useCallback, useEffect, useRef, useState } from "react";
import * as Tone from "tone";
import { VoicePitchTracker, type RawPitchFrame } from "./pitchDetector";
import { kickAudio } from "./piano";

/**
 * Microphone → discrete note events.
 *
 * The YIN tracker emits a continuous stream of fractional-MIDI frames roughly
 * every 25 ms. A scorer wants NOTES, so this turns the stream into onsets:
 *
 *  • a pitch must hold within a semitone for MIN_STABLE_MS before it counts —
 *    without that, the glide into a note fires two or three wrong onsets;
 *  • the same note does not re-fire until the singer leaves it (or a gap of
 *    silence), so one held note is one event, not forty;
 *  • frames below the noise floor are silence, which is what re-arms the
 *    repeat of a note.
 *
 * Works in every browser with a microphone — which is the point: Safari and
 * iPad have no Web MIDI at all, and an acoustic piano has no MIDI either.
 */

export type MicStatus = "idle" | "starting" | "listening" | "error";

export interface MicNote {
  /** Rounded MIDI number of the detected note. */
  midi: number;
  /** Pitch class 0..11. */
  pc: number;
  /** How far from the centre of the note, in cents (−50..50). */
  cents: number;
}

/** A pitch must be this stable before it is called a note. */
const MIN_STABLE_MS = 60;
/** Below this RMS the input is treated as silence. */
const SILENCE_RMS = 0.008;

export function useMicNotes(onNote: (n: MicNote) => void) {
  const [status, setStatus] = useState<MicStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null);
  /** Live fractional MIDI, for a tuner readout. */
  const [liveMidi, setLiveMidi] = useState<number | null>(null);

  const trackerRef = useRef<VoicePitchTracker | null>(null);
  const onNoteRef = useRef(onNote);
  onNoteRef.current = onNote;

  // Onset state, kept in refs so the audio path never waits on a render.
  const candidate = useRef<{ midi: number; since: number } | null>(null);
  const sounding = useRef<number | null>(null);

  const handleFrame = useCallback((frame: RawPitchFrame) => {
    if (frame.midi == null || frame.rms < SILENCE_RMS) {
      // Silence re-arms the note, so a repeated pitch can fire again.
      candidate.current = null;
      sounding.current = null;
      setLiveMidi(null);
      return;
    }
    setLiveMidi(frame.midi);
    const rounded = Math.round(frame.midi);

    if (!candidate.current || candidate.current.midi !== rounded) {
      candidate.current = { midi: rounded, since: frame.t };
      return;
    }
    const heldMs = (frame.t - candidate.current.since) * 1000;
    if (heldMs < MIN_STABLE_MS) return;
    if (sounding.current === rounded) return; // already counted this one

    sounding.current = rounded;
    onNoteRef.current({
      midi: rounded,
      pc: ((rounded % 12) + 12) % 12,
      cents: Math.round((frame.midi - rounded) * 100),
    });
  }, []);

  const start = useCallback(async () => {
    if (trackerRef.current) return;
    setStatus("starting");
    setError(null);
    try {
      // Share the app's existing AudioContext rather than opening a second
      // one — two contexts on iOS is a reliable way to get silence.
      kickAudio();
      await Tone.start();
      const ctx = Tone.getContext().rawContext as unknown as AudioContext;
      const tracker = new VoicePitchTracker(ctx);
      await tracker.initFromMic();
      tracker.start(handleFrame);
      trackerRef.current = tracker;
      setDeviceLabel(tracker.activeLabel ?? null);
      setStatus("listening");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open the microphone");
      setStatus("error");
      trackerRef.current = null;
    }
  }, [handleFrame]);

  const stop = useCallback(() => {
    trackerRef.current?.stop();
    trackerRef.current = null;
    candidate.current = null;
    sounding.current = null;
    setLiveMidi(null);
    setStatus("idle");
  }, []);

  useEffect(() => () => { trackerRef.current?.stop(); trackerRef.current = null; }, []);

  return { status, error, deviceLabel, liveMidi, start, stop };
}
